import { getCached, setCache } from './cache';
import type {
  GSCDateRange,
  GSCRow,
  GSCQueryRow,
  GSCPageRow,
  GSCDateRow,
  GSCDeviceRow,
  GSCCountryRow,
  GSCSummary,
} from '../types/searchConsole';

interface GSCApiResponse {
  rows?: GSCRow[];
  responseAggregationType?: string;
}

function getDateRange(range: GSCDateRange): { startDate: string; endDate: string } {
  const end = new Date();
  end.setDate(end.getDate() - 3); // GSC data has ~3-day lag
  const start = new Date(end);

  switch (range) {
    case '7d':
      start.setDate(start.getDate() - 7);
      break;
    case '28d':
      start.setDate(start.getDate() - 28);
      break;
    case '3m':
      start.setMonth(start.getMonth() - 3);
      break;
    case '6m':
      start.setMonth(start.getMonth() - 6);
      break;
    case '12m':
      start.setMonth(start.getMonth() - 12);
      break;
    case '16m':
      start.setMonth(start.getMonth() - 16);
      break;
  }

  return {
    startDate: start.toISOString().split('T')[0],
    endDate: end.toISOString().split('T')[0],
  };
}

async function queryGSC(body: Record<string, unknown>, signal?: AbortSignal): Promise<GSCApiResponse> {
  const res = await fetch('/api/gsc/search-analytics', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok) {
    const errBody = await res.text();
    throw new Error(`GSC API error ${res.status}: ${errBody}`);
  }

  return res.json();
}

function computeSummary(rows: GSCRow[]): GSCSummary {
  if (!rows.length) {
    return { totalClicks: 0, totalImpressions: 0, avgCTR: 0, avgPosition: 0 };
  }

  const totalClicks = rows.reduce((s, r) => s + r.clicks, 0);
  const totalImpressions = rows.reduce((s, r) => s + r.impressions, 0);
  const avgCTR = totalImpressions > 0 ? totalClicks / totalImpressions : 0;
  const avgPosition =
    totalImpressions > 0
      ? rows.reduce((s, r) => s + r.position * r.impressions, 0) / totalImpressions
      : 0;

  return { totalClicks, totalImpressions, avgCTR, avgPosition };
}

export async function fetchDailyTrend(
  range: GSCDateRange,
  signal?: AbortSignal
): Promise<{ rows: GSCDateRow[]; summary: GSCSummary }> {
  const cacheKey = `gsc:daily:${range}`;
  const cached = getCached<{ rows: GSCDateRow[]; summary: GSCSummary }>(cacheKey);
  if (cached) return cached;

  const { startDate, endDate } = getDateRange(range);
  const data = await queryGSC(
    { startDate, endDate, dimensions: ['date'], type: 'web' },
    signal
  );

  const rows: GSCDateRow[] = (data.rows || []).map((r) => ({
    date: r.keys[0],
    clicks: r.clicks,
    impressions: r.impressions,
    ctr: r.ctr,
    position: r.position,
  }));

  const summary = computeSummary(data.rows || []);
  const result = { rows, summary };
  setCache(cacheKey, result);
  return result;
}

export async function fetchTopQueries(
  range: GSCDateRange,
  signal?: AbortSignal
): Promise<GSCQueryRow[]> {
  const cacheKey = `gsc:queries:${range}`;
  const cached = getCached<GSCQueryRow[]>(cacheKey);
  if (cached) return cached;

  const { startDate, endDate } = getDateRange(range);
  const data = await queryGSC(
    { startDate, endDate, dimensions: ['query'], type: 'web', rowLimit: 500 },
    signal
  );

  const rows: GSCQueryRow[] = (data.rows || []).map((r) => ({
    query: r.keys[0],
    clicks: r.clicks,
    impressions: r.impressions,
    ctr: r.ctr,
    position: r.position,
  }));

  setCache(cacheKey, rows);
  return rows;
}

export async function fetchTopPages(
  range: GSCDateRange,
  signal?: AbortSignal
): Promise<GSCPageRow[]> {
  const cacheKey = `gsc:pages:${range}`;
  const cached = getCached<GSCPageRow[]>(cacheKey);
  if (cached) return cached;

  const { startDate, endDate } = getDateRange(range);
  const data = await queryGSC(
    { startDate, endDate, dimensions: ['page'], type: 'web', rowLimit: 500 },
    signal
  );

  const rows: GSCPageRow[] = (data.rows || []).map((r) => ({
    page: r.keys[0],
    clicks: r.clicks,
    impressions: r.impressions,
    ctr: r.ctr,
    position: r.position,
  }));

  setCache(cacheKey, rows);
  return rows;
}

export async function fetchDeviceBreakdown(
  range: GSCDateRange,
  signal?: AbortSignal
): Promise<GSCDeviceRow[]> {
  const cacheKey = `gsc:devices:${range}`;
  const cached = getCached<GSCDeviceRow[]>(cacheKey);
  if (cached) return cached;

  const { startDate, endDate } = getDateRange(range);
  const data = await queryGSC(
    { startDate, endDate, dimensions: ['device'], type: 'web' },
    signal
  );

  const rows: GSCDeviceRow[] = (data.rows || []).map((r) => ({
    device: r.keys[0],
    clicks: r.clicks,
    impressions: r.impressions,
    ctr: r.ctr,
    position: r.position,
  }));

  setCache(cacheKey, rows);
  return rows;
}

export async function fetchCountryBreakdown(
  range: GSCDateRange,
  signal?: AbortSignal
): Promise<GSCCountryRow[]> {
  const cacheKey = `gsc:countries:${range}`;
  const cached = getCached<GSCCountryRow[]>(cacheKey);
  if (cached) return cached;

  const { startDate, endDate } = getDateRange(range);
  const data = await queryGSC(
    { startDate, endDate, dimensions: ['country'], type: 'web', rowLimit: 50 },
    signal
  );

  const rows: GSCCountryRow[] = (data.rows || []).map((r) => ({
    country: r.keys[0],
    clicks: r.clicks,
    impressions: r.impressions,
    ctr: r.ctr,
    position: r.position,
  }));

  setCache(cacheKey, rows);
  return rows;
}
